import { Icon } from '@blueprintjs/core';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatBytes } from 'Util';
import styles from './DropOrChooseFilePicker.module.css';

const DropOrChooseFilePicker = ({ onFilesSelected }) => {
  const dropZoneRef = useRef(null);
  const [fileList, setFileList] = useState([]);

  const onDragEnter = () => dropZoneRef.current.classList.add(styles.dragover);
  const onDragLeave = () =>
    dropZoneRef.current.classList.remove(styles.dragover);

  useEffect(() => {
    onFilesSelected && onFilesSelected(fileList);
  }, [fileList, onFilesSelected]);

  const fileIsNotAlreadySelected = useCallback(
    (fileToAdd) => {
      return (
        !fileList?.length ||
        !fileList.some(
          (existingFile) =>
            existingFile.name === fileToAdd.name &&
            existingFile.size === fileToAdd.size &&
            existingFile.type === fileToAdd.type,
        )
      );
    },
    [fileList],
  );

  const handleDrop = (event) => {
    event.preventDefault();

    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles?.length > 0) {
      const newFiles = Array.from(droppedFiles).filter(
        fileIsNotAlreadySelected,
      );
      setFileList((prevFiles) => [...prevFiles, ...newFiles]);
    }
    dropZoneRef.current.classList.remove(styles.dragover);
  };

  const handleFileChange = (event) => {
    const selectedFiles = event.target.files;
    if (selectedFiles?.length > 0) {
      const newFiles = Array.from(selectedFiles).filter(
        fileIsNotAlreadySelected,
      );
      setFileList((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  const fileRemove = (file) => {
    const updatedList = [...fileList];
    updatedList.splice(fileList.indexOf(file), 1);
    setFileList(updatedList);
  };

  const getFileName = (fileName, getExt) => {
    let name = fileName.split('.');
    let extension = `.${name.pop()}`;
    name = name.join('.');
    return getExt ? extension : name;
  };

  return (
    <div className={styles.file_picker_container}>
      <div
        ref={dropZoneRef}
        className={styles.drop_zone}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={handleDrop}
        onDragOver={(ev) => ev.preventDefault()}>
        <div>Drop file(s) here</div>
        <div> or </div>
        <label htmlFor={'file_picker'} className={styles.fp_label}>
          Select file(s)...
        </label>
        <input
          multiple
          id='file_picker'
          type='file'
          onChange={handleFileChange}
          style={{ display: 'none' }}></input>
      </div>
      {fileList.length > 0 ? (
        <div className={styles.selected_files_preview}>
          <p className={styles.selected_files_label}>Selected Files</p>
          {fileList.map((item, index) => (
            <div key={index} className={styles.selected_file}>
              <Icon icon={'document'} size={24} />
              <div className={styles.selected_file_info}>
                <p className={styles.selected_file_name}>
                  {getFileName(item.name)}
                </p>
                <p className={styles.selected_file_ext}>
                  {getFileName(item.name, true)}
                </p>
                <p className={styles.selected_file_size}>
                  {formatBytes(item.size)}
                </p>
              </div>
              <DeleteButton
                className={styles.selected_file_del}
                miniVersion
                useXIcon
                onDeleteItem={() => fileRemove(item)}
                tooltipText={'Remove File'}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

DropOrChooseFilePicker.propTypes = {
  onFilesSelected: PropTypes.func,
};

export default DropOrChooseFilePicker;
